import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/apiClient";
import { fetchAuthMe, clearSharedCache } from "../lib/sharedGet";
import { clearSnapshots, setSnapshotOwner } from "../lib/lastKnown";

const AuthContext = createContext(null);

// Idle auto-logout config. Activity is shared across tabs via localStorage so
// an active tab keeps an idle one alive; the heartbeat keeps the SERVER window
// in sync (the server independently enforces the same 10-minute idle limit).
const IDLE_LIMIT_MS = 10 * 60 * 1000;
const HEARTBEAT_THROTTLE_MS = 60 * 1000;
const IDLE_CHECK_MS = 15 * 1000;
const ACTIVITY_KEY = "tlb_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastHeartbeatRef = useRef(0);
  const lastIdentityRef = useRef(null);

  // `refresh()` from a page (login, register, email verify, profile save) is an
  // explicit request for fresh data, so it bypasses the shared cache. The mount
  // check below is the opposite: it happily reuses an in-flight/cached
  // response, which is what collapses the StrictMode replay and the other
  // `/auth/me` readers.
  //
  // Identity swap (a different account in the same tab) is the one case where
  // everything else cached — billing, admin probe — must go. Clearing on the
  // identity change rather than on every forced refresh keeps a profile save
  // from cancelling out an in-flight `/billing/me` on the same tick.
  const checkAuth = useCallback(async ({ force = true } = {}) => {
    try {
      const { data } = await fetchAuthMe(api, { force });
      const identity = data?.user_id || data?.email || null;
      if (identity && lastIdentityRef.current && identity !== lastIdentityRef.current) {
        clearSharedCache();
      }
      // Persisted, so a session swap that goes through a full page reload
      // (browser restart, OAuth return) still wipes the previous account's
      // snapshots — the in-memory ref above is reset by the reload.
      setSnapshotOwner(identity);
      lastIdentityRef.current = identity;
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth({ force: false });
  }, [checkAuth]);

  // Instant re-sync: when a signed-in user returns to the tab, re-pull their
  // entitlements so an advisor client sees newly unlocked (or changed) feature
  // access the moment their advisor's plan changes — no reload, no wait.
  // Throttled to once per 20s so routine tab switches don't hammer the API.
  const lastFocusSyncRef = useRef(0);
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastFocusSyncRef.current < 20000) return;
      lastFocusSyncRef.current = now;
      if (lastIdentityRef.current) checkAuth({ force: true });
    };
    window.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [checkAuth]);

  const logout = useCallback(async (opts = {}) => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    clearSharedCache();
    clearSnapshots();
    setSnapshotOwner(null);
    lastIdentityRef.current = null;
    try {
      localStorage.removeItem(ACTIVITY_KEY);
    } catch {
      /* ignore */
    }
    if (opts.reason) {
      window.location.replace(`/login?reason=${opts.reason}`);
    }
  }, []);

  // Idle auto-logout — runs only while authenticated.
  useEffect(() => {
    if (!user) return undefined;

    const bump = () => {
      try {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    };
    bump();

    const onActivity = () => {
      bump();
      const now = Date.now();
      if (now - lastHeartbeatRef.current > HEARTBEAT_THROTTLE_MS) {
        lastHeartbeatRef.current = now;
        api.post("/auth/heartbeat").catch(() => {});
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const interval = setInterval(() => {
      let last;
      try {
        last = Number(localStorage.getItem(ACTIVITY_KEY));
      } catch {
        last = Date.now();
      }
      if (!last) last = Date.now();
      if (Date.now() - last > IDLE_LIMIT_MS) {
        logout({ reason: "inactivity" });
      }
    }, IDLE_CHECK_MS);

    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
